import React, { useEffect, useState } from "react";
import axios from "axios";

import {
    FaTimes,
    FaSpinner,
    FaUser,
    FaPhone,
    FaEnvelope,
    FaIdCard,
    FaVenusMars,
    FaLink,
    FaUsers,
    FaMoneyBillWave,
    FaWallet,
    FaCalendarAlt,
    FaCheckCircle,
    FaTimesCircle,
    FaCopy,
    FaExternalLinkAlt,
    FaArrowLeft,
    FaArrowRight,
    FaLayerGroup,
    FaTrophy,
    FaChevronRight,
    FaShieldAlt,
    FaCoins,
} from "react-icons/fa";

const UserDetails = ({ user, onClose, isDarkMode }) => {
    const [userData, setUserData] = useState(null);
    const [referralStats, setReferralStats] = useState(null);
    const [salaryData, setSalaryData] = useState(null);

    const [referrerData, setReferrerData] = useState(null);
    const [linkOwnerData, setLinkOwnerData] = useState(null);
    const [groupData, setGroupData] = useState(null);
    const [sideData, setSideData] = useState(null);

    const [leftSalary, setLeftSalary] = useState(0);
    const [rightSalary, setRightSalary] = useState(0);

    const [leftEarnings, setLeftEarnings] = useState(0);
    const [rightEarnings, setRightEarnings] = useState(0);

    const [loading, setLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState("");
    const [copied, setCopied] = useState(false);

    const apiBaseUrl = (
        process.env.REACT_APP_API_BASE_URL ||
        "https://artim.shop/backendtest_9"
    ).replace(/\/$/, "");

    const getResponseData = (response) => {
        if (!response) return null;

        if (response.data !== undefined) {
            return response.data;
        }

        return response;
    };

    const getPersonData = (response) => {
        const data = getResponseData(response);

        if (!data || typeof data !== "object") {
            return null;
        }

        if (
            data.referrerName ||
            data.referrerEmail ||
            data.referrerPhoto ||
            data.referrerReferralCode
        ) {
            return data;
        }

        if (data.data && typeof data.data === "object") {
            if (
                data.data.referrerName ||
                data.data.referrerEmail ||
                data.data.referrerPhoto ||
                data.data.referrerReferralCode
            ) {
                return data.data;
            }
        }

        if (data.referrer && typeof data.referrer === "object") {
            return data.referrer;
        }

        if (data.user && typeof data.user === "object") {
            return data.user;
        }

        return data;
    };

    const getNumber = (...values) => {
        for (const value of values) {
            if (
                value === null ||
                value === undefined ||
                value === ""
            ) {
                continue;
            }

            const number = Number(value);

            if (Number.isFinite(number)) {
                return number;
            }
        }

        return 0;
    };

    const formatMoney = (value) => {
        const number = getNumber(value);

        return `${number.toLocaleString("az-AZ", {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
        })} AZN`;
    };

    const formatDate = (date) => {
        if (!date) return "-";

        const parsedDate = new Date(date);

        if (Number.isNaN(parsedDate.getTime())) {
            return "-";
        }

        return parsedDate.toLocaleDateString("az-AZ", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
        });
    };

    const getValue = (obj, keys, fallback = "-") => {
        if (!obj || typeof obj !== "object") {
            return fallback;
        }

        for (const key of keys) {
            const value = obj[key];

            if (
                value !== undefined &&
                value !== null &&
                value !== ""
            ) {
                return value;
            }
        }

        return fallback;
    };

    const getSalaryValue = () => {
        if (typeof salaryData === "number") {
            return salaryData;
        }

        if (!salaryData || typeof salaryData !== "object") {
            return 0;
        }

        return getNumber(
            salaryData.salary,
            salaryData.maas,
            salaryData.maaş,
            salaryData.amount,
            salaryData.totalSalary,
            salaryData.total
        );
    };

    const getReferralEarnings = () => {
        if (typeof referralStats === "number") {
            return referralStats;
        }

        if (!referralStats || typeof referralStats !== "object") {
            return 0;
        }

        return getNumber(
            referralStats.totalEarned,
            referralStats.earnings,
            referralStats.totalEarnedAmount,
            referralStats.totalProfit,
            referralStats.amount,
            referralStats.total
        );
    };

    const getReferralCount = () => {
        return getNumber(
            referralStats?.referralCount,
            referralStats?.count,
            referralStats?.totalUsers,
            referralStats?.usersCount,
            userData?.referralCount,
            user?.referralCount
        );
    };

    const getGroupCount = () => {
        return getNumber(
            groupData?.count,
            groupData?.groupCount,
            groupData?.referralCount,
            groupData?.totalUsers,
            groupData?.usersCount,
            groupData?.total
        );
    };

    const getRank = () => {
        if (!salaryData || typeof salaryData !== "object") {
            return "-";
        }

        return getValue(
            salaryData,
            ["rank", "rütbə", "rutbe"],
            "-"
        );
    };

    const getSideText = (side) => {
        if (side === "left") return "Sol";
        if (side === "right") return "Sağ";

        return "Təyin edilməyib";
    };

    const getPhotoUrl = (photo) => {
        if (!photo) return null;

        if (
            typeof photo === "string" &&
            (
                photo.startsWith("http://") ||
                photo.startsWith("https://") ||
                photo.startsWith("data:") ||
                photo.startsWith("blob:")
            )
        ) {
            return photo;
        }

        if (typeof photo === "string") {
            return `${apiBaseUrl}/${photo.replace(/^\/+/, "")}`;
        }

        return null;
    };

    const normalizeSideResponse = (response) => {
        const source = getResponseData(response);

        if (!source) {
            return {
                left: [],
                right: [],
            };
        }

        const makeArray = (value) => {
            if (!value) return [];

            if (Array.isArray(value)) {
                return value.filter(Boolean);
            }

            if (typeof value === "object") {
                return [value];
            }

            return [];
        };

        const findByKeys = (obj, keys) => {
            if (!obj || typeof obj !== "object") {
                return [];
            }

            for (const key of keys) {
                if (
                    obj[key] !== undefined &&
                    obj[key] !== null
                ) {
                    return makeArray(obj[key]);
                }
            }

            return [];
        };

        let left = findByKeys(source, [
            "left",
            "leftUsers",
            "leftUser",
            "sol",
            "solUsers",
            "solUser",
            "leftSide",
        ]);

        let right = findByKeys(source, [
            "right",
            "rightUsers",
            "rightUser",
            "sag",
            "sagUsers",
            "sagUser",
            "rightSide",
        ]);

        if (left.length === 0 && right.length === 0) {
            let possibleUsers = [];

            if (Array.isArray(source)) {
                possibleUsers = source;
            } else {
                possibleUsers =
                    source.users ||
                    source.members ||
                    source.data ||
                    source.results ||
                    source.items ||
                    [];
            }

            if (Array.isArray(possibleUsers)) {
                left = possibleUsers.filter(
                    (item) => item?.side === "left"
                );

                right = possibleUsers.filter(
                    (item) => item?.side === "right"
                );
            }
        }

        return {
            left,
            right,
        };
    };

    const loadData = async () => {
        if (!user) {
            setLoading(false);
            return;
        }

        setLoading(true);
        setErrorMessage("");

        try {
            const userId = user?._id || user?.id;
            const referralCode = user?.referralCode;

            if (!userId) {
                throw new Error("İstifadəçi ID-si tapılmadı.");
            }

            const requests = [];

            requests.push({
                key: "user",
                request: axios.get(
                    `${apiBaseUrl}/api/users/getuser/${encodeURIComponent(
                        userId
                    )}`,
                    {
                        withCredentials: true,
                    }
                ),
            });

            if (referralCode) {
                requests.push({
                    key: "referralStats",
                    request: axios.get(
                        `${apiBaseUrl}/api/users/referral-stats/${encodeURIComponent(
                            referralCode
                        )}`,
                        {
                            withCredentials: true,
                        }
                    ),
                });

                requests.push({
                    key: "salary",
                    request: axios.get(
                        `${apiBaseUrl}/api/users/salary/${encodeURIComponent(
                            referralCode
                        )}`,
                        {
                            withCredentials: true,
                        }
                    ),
                });

                requests.push({
                    key: "referrer",
                    request: axios.get(
                        `${apiBaseUrl}/api/users/referredBykod/${encodeURIComponent(
                            referralCode
                        )}`,
                        {
                            withCredentials: true,
                        }
                    ),
                });

                requests.push({
                    key: "linkOwner",
                    request: axios.get(
                        `${apiBaseUrl}/api/users/get-link-owner/${encodeURIComponent(
                            referralCode
                        )}`,
                        {
                            withCredentials: true,
                        }
                    ),
                });

                requests.push({
                    key: "group",
                    request: axios.get(
                        `${apiBaseUrl}/api/users/user/${encodeURIComponent(
                            referralCode
                        )}`,
                        {
                            withCredentials: true,
                        }
                    ),
                });

                requests.push({
                    key: "side",
                    request: axios.get(
                        `${apiBaseUrl}/api/users/admin/${encodeURIComponent(
                            referralCode
                        )}`,
                        {
                            withCredentials: true,
                        }
                    ),
                });
            }

            requests.push({
                key: "leftSalary",
                request: axios.get(
                    `${apiBaseUrl}/api/my-team/branch-salary/${encodeURIComponent(
                        user.referralCode
                    )}/left`,
                    {
                        withCredentials: true,
                    }
                ),
            });

            requests.push({
                key: "rightSalary",
                request: axios.get(
                    `${apiBaseUrl}/api/my-team/branch-salary/${encodeURIComponent(
                        user.referralCode
                    )}/right`,
                    {
                        withCredentials: true,
                    }
                ),
            });

            requests.push({
                key: "leftEarnings",
                request: axios.get(
                    `${apiBaseUrl}/api/my-team/branch-earnings/${encodeURIComponent(
                        user.referralCode
                    )}/left`,
                    {
                        withCredentials: true,
                    }
                ),
            });

            requests.push({
                key: "rightEarnings",
                request: axios.get(
                    `${apiBaseUrl}/api/my-team/branch-earnings/${encodeURIComponent(
                        user.referralCode
                    )}/right`,
                    {
                        withCredentials: true,
                    }
                ),
            });

            const results = await Promise.allSettled(
                requests.map((item) => item.request)
            );

            const resultMap = {};

            results.forEach((result, index) => {
                const key = requests[index].key;

                if (result.status === "fulfilled") {
                    resultMap[key] = result.value;
                } else {
                    console.error(
                        `${key} API xətası:`,
                        result.reason
                    );

                    resultMap[key] = null;
                }
            });

            if (resultMap.user) {
                setUserData(
                    getResponseData(resultMap.user)
                );
            } else {
                setUserData(user);
            }

            if (resultMap.referralStats) {
                setReferralStats(
                    getResponseData(
                        resultMap.referralStats
                    )
                );
            } else {
                setReferralStats(null);
            }

            if (resultMap.salary) {
                setSalaryData(
                    getResponseData(resultMap.salary)
                );
            } else {
                setSalaryData(null);
            }

            if (resultMap.referrer) {
                setReferrerData(
                    getPersonData(resultMap.referrer)
                );
            } else {
                setReferrerData(null);
            }

            if (resultMap.linkOwner) {
                setLinkOwnerData(
                    getPersonData(resultMap.linkOwner)
                );
            } else {
                setLinkOwnerData(null);
            }

            if (resultMap.group) {
                setGroupData(
                    getResponseData(resultMap.group)
                );
            } else {
                setGroupData(null);
            }

            if (resultMap.side) {
                setSideData(
                    normalizeSideResponse(resultMap.side)
                );
            } else {
                setSideData({
                    left: [],
                    right: [],
                });
            }

            if (resultMap.leftSalary) {
                const data = getResponseData(
                    resultMap.leftSalary
                );

                setLeftSalary(
                    getNumber(
                        data?.totalSalary,
                        data?.salary,
                        data?.total
                    )
                );
            } else {
                setLeftSalary(0);
            }

            if (resultMap.rightSalary) {
                const data = getResponseData(
                    resultMap.rightSalary
                );

                setRightSalary(
                    getNumber(
                        data?.totalSalary,
                        data?.salary,
                        data?.total
                    )
                );
            } else {
                setRightSalary(0);
            }

            if (resultMap.leftEarnings) {
                const data = getResponseData(
                    resultMap.leftEarnings
                );

                setLeftEarnings(
                    getNumber(
                        data?.totalEarnings,
                        data?.totalEarned,
                        data?.total
                    )
                );
            } else {
                setLeftEarnings(0);
            }

            if (resultMap.rightEarnings) {
                const data = getResponseData(
                    resultMap.rightEarnings
                );

                setRightEarnings(
                    getNumber(
                        data?.totalEarnings,
                        data?.totalEarned,
                        data?.total
                    )
                );
            } else {
                setRightEarnings(0);
            }
        } catch (error) {
            console.error(
                "UserDetails error:",
                error
            );

            setErrorMessage(
                error?.response?.data?.message ||
                error?.message ||
                "Məlumatları yükləmək mümkün olmadı."
            );

            setUserData(user);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, [user]);

    const displayUser =
        userData ||
        user ||
        {};

    const payment = Boolean(
        displayUser.payment
    );

    const salary = getSalaryValue();
    const referralEarnings =
        getReferralEarnings();

    const totalPersonalEarnings =
        salary +
        referralEarnings;

    const totalGroupSalary =
        leftSalary +
        rightSalary;

    const totalGroupEarnings =
        leftEarnings +
        rightEarnings;

    const referralLink =
        displayUser.referralCode
            ? `https://artim.shop/register?referral=${displayUser.referralCode}`
            : "-";

    const copyReferralLink =
        async () => {
            if (
                !displayUser.referralCode
            )
                return;

            try {
                await navigator.clipboard.writeText(
                    referralLink
                );

                setCopied(true);

                setTimeout(() => {
                    setCopied(false);
                }, 2000);
            } catch (error) {
                console.error(
                    "Link kopyalama xətası:",
                    error
                );
            }
        };

    const leftMembers =
        sideData?.left || [];

    const rightMembers =
        sideData?.right || [];

    const profilePhoto =
        getPhotoUrl(
            displayUser.photo
        );

    /*
    ============================================================
    PREMIUM COMPONENTS
    ============================================================
    */

    const SectionTitle = ({
        icon: Icon,
        title,
        description,
    }) => (
        <div className="mb-5 flex items-center gap-3">
            <div
                className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl ${
                    isDarkMode
                        ? "bg-white/5 text-blue-400 ring-1 ring-white/10"
                        : "bg-blue-50 text-blue-600 ring-1 ring-blue-100"
                }`}
            >
                <Icon />
            </div>

            <div className="min-w-0">
                <h3 className="text-lg font-black tracking-tight">
                    {title}
                </h3>

                {description && (
                    <p
                        className={`mt-0.5 text-xs ${
                            isDarkMode
                                ? "text-slate-500"
                                : "text-slate-500"
                        }`}
                    >
                        {description}
                    </p>
                )}
            </div>
        </div>
    );

    const InfoItem = ({
        icon: Icon,
        label,
        value,
        children,
    }) => (
        <div
            className={`group rounded-2xl border p-4 transition-all duration-300 hover:-translate-y-0.5 ${
                isDarkMode
                    ? "border-white/10 bg-white/[0.035] hover:border-blue-500/30 hover:bg-white/[0.055]"
                    : "border-slate-200 bg-white hover:border-blue-200 hover:shadow-lg hover:shadow-blue-100/40"
            }`}
        >
            <div className="flex items-start gap-3">
                <div
                    className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl transition ${
                        isDarkMode
                            ? "bg-blue-500/10 text-blue-400 group-hover:bg-blue-500/20"
                            : "bg-blue-50 text-blue-600 group-hover:bg-blue-100"
                    }`}
                >
                    <Icon />
                </div>

                <div className="min-w-0 flex-1">
                    <p
                        className={`mb-1 text-[10px] font-bold uppercase tracking-wider ${
                            isDarkMode
                                ? "text-slate-500"
                                : "text-slate-400"
                        }`}
                    >
                        {label}
                    </p>

                    {children || (
                        <p
                            className={`break-words text-sm font-bold ${
                                isDarkMode
                                    ? "text-slate-100"
                                    : "text-slate-800"
                            }`}
                        >
                            {value}
                        </p>
                    )}
                </div>
            </div>
        </div>
    );

    const StatCard = ({
        icon: Icon,
        title,
        value,
        subtitle,
        iconClass,
        iconBg,
    }) => (
        <div
            className={`group relative overflow-hidden rounded-3xl border p-5 transition-all duration-300 hover:-translate-y-1 ${
                isDarkMode
                    ? "border-white/10 bg-white/[0.035] hover:border-white/15 hover:bg-white/[0.055]"
                    : "border-slate-200 bg-white hover:-translate-y-1 hover:shadow-xl hover:shadow-slate-200/50"
            }`}
        >
            <div
                className={`absolute -right-8 -top-8 h-24 w-24 rounded-full blur-2xl opacity-20 ${iconBg}`}
            />

            <div className="relative">
                <div className="flex items-center justify-between">
                    <div
                        className={`flex h-12 w-12 items-center justify-center rounded-2xl ${iconBg} ${iconClass}`}
                    >
                        <Icon />
                    </div>

                    <FaChevronRight
                        className={`text-xs transition-transform duration-300 group-hover:translate-x-1 ${
                            isDarkMode
                                ? "text-slate-700"
                                : "text-slate-300"
                        }`}
                    />
                </div>

                <p
                    className={`mt-5 text-xs font-semibold ${
                        isDarkMode
                            ? "text-slate-500"
                            : "text-slate-500"
                    }`}
                >
                    {title}
                </p>

                <p className="mt-1 truncate text-2xl font-black tracking-tight">
                    {value}
                </p>

                {subtitle && (
                    <p
                        className={`mt-1 text-[11px] ${
                            isDarkMode
                                ? "text-slate-600"
                                : "text-slate-400"
                        }`}
                    >
                        {subtitle}
                    </p>
                )}
            </div>
        </div>
    );

    const PersonCard = ({
        title,
        person,
    }) => {
        if (!person) {
            return (
                <div
                    className={`rounded-3xl border p-5 ${
                        isDarkMode
                            ? "border-white/10 bg-white/[0.025]"
                            : "border-slate-200 bg-white"
                    }`}
                >
                    <div className="mb-4 flex items-center gap-3">
                        <div
                            className={`flex h-10 w-10 items-center justify-center rounded-xl ${
                                isDarkMode
                                    ? "bg-white/5 text-slate-500"
                                    : "bg-slate-100 text-slate-400"
                            }`}
                        >
                            <FaUser />
                        </div>

                        <div>
                            <p className="text-sm font-black">
                                {title}
                            </p>

                            <p
                                className={`text-[10px] ${
                                    isDarkMode
                                        ? "text-slate-600"
                                        : "text-slate-400"
                                }`}
                            >
                                İstinad məlumatı
                            </p>
                        </div>
                    </div>

                    <p
                        className={`text-sm ${
                            isDarkMode
                                ? "text-slate-600"
                                : "text-slate-400"
                        }`}
                    >
                        Məlumat yoxdur
                    </p>
                </div>
            );
        }

        const name =
            person.referrerName ||
            person.name ||
            "-";

        const email =
            person.referrerEmail ||
            person.email ||
            "-";

        const referralCode =
            person.referrerReferralCode ||
            person.referralCode ||
            "-";

        const photo =
            person.referrerPhoto ||
            person.photo;

        const photoUrl =
            getPhotoUrl(photo);

        return (
            <div
                className={`group rounded-3xl border p-5 transition-all duration-300 ${
                    isDarkMode
                        ? "border-white/10 bg-white/[0.025] hover:border-blue-500/20"
                        : "border-slate-200 bg-white hover:border-blue-200 hover:shadow-lg"
                }`}
            >
                <div className="mb-5 flex items-center gap-3">
                    {photoUrl ? (
                        <img
                            src={photoUrl}
                            alt={name}
                            className="h-14 w-14 rounded-2xl object-cover ring-2 ring-blue-500/20"
                        />
                    ) : (
                        <div
                            className={`flex h-14 w-14 items-center justify-center rounded-2xl ${
                                isDarkMode
                                    ? "bg-slate-800 text-slate-400"
                                    : "bg-slate-100 text-slate-400"
                            }`}
                        >
                            <FaUser />
                        </div>
                    )}

                    <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-black">
                            {name}
                        </p>

                        <div className="mt-1 flex items-center gap-1.5">
                            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />

                            <p
                                className={`truncate text-[10px] font-semibold ${
                                    isDarkMode
                                        ? "text-slate-500"
                                        : "text-slate-400"
                                }`}
                            >
                                {title}
                            </p>
                        </div>
                    </div>
                </div>

                <div className="grid gap-3">
                    <div
                        className={`rounded-xl p-3 ${
                            isDarkMode
                                ? "bg-black/10"
                                : "bg-slate-50"
                        }`}
                    >
                        <p
                            className={`text-[9px] font-bold uppercase tracking-wider ${
                                isDarkMode
                                    ? "text-slate-600"
                                    : "text-slate-400"
                            }`}
                        >
                            Email
                        </p>

                        <p
                            className={`mt-1 break-all text-xs font-semibold ${
                                isDarkMode
                                    ? "text-slate-300"
                                    : "text-slate-700"
                            }`}
                        >
                            {email}
                        </p>
                    </div>

                    <div
                        className={`rounded-xl p-3 ${
                            isDarkMode
                                ? "bg-blue-500/5"
                                : "bg-blue-50/70"
                        }`}
                    >
                        <p
                            className={`text-[9px] font-bold uppercase tracking-wider ${
                                isDarkMode
                                    ? "text-blue-500"
                                    : "text-blue-500"
                            }`}
                        >
                            Referral kodu
                        </p>

                        <p className="mt-1 break-all text-xs font-black text-blue-500">
                            {referralCode}
                        </p>
                    </div>
                </div>
            </div>
        );
    };

    const SideUserCard = ({
        member,
        side,
    }) => {
        if (!member) return null;

        const name =
            member.name ||
            member.referrerName ||
            "-";

        const email =
            member.email ||
            member.referrerEmail ||
            "-";

        const referralCode =
            member.referralCode ||
            member.referrerReferralCode ||
            "-";

        const photo =
            member.photo ||
            member.referrerPhoto;

        const photoUrl =
            getPhotoUrl(photo);

        const isLeft =
            side === "left";

        return (
            <div
                className={`group rounded-2xl border p-4 transition-all duration-300 hover:-translate-y-0.5 ${
                    isDarkMode
                        ? "border-white/10 bg-white/[0.025] hover:bg-white/[0.05]"
                        : "border-slate-200 bg-white hover:shadow-md"
                }`}
            >
                <div className="flex items-center gap-3">
                    {photoUrl ? (
                        <img
                            src={photoUrl}
                            alt={name}
                            className="h-12 w-12 rounded-2xl object-cover ring-2 ring-black/5"
                        />
                    ) : (
                        <div
                            className={`flex h-12 w-12 items-center justify-center rounded-2xl ${
                                isDarkMode
                                    ? "bg-slate-800 text-slate-400"
                                    : "bg-slate-100 text-slate-400"
                            }`}
                        >
                            <FaUser />
                        </div>
                    )}

                    <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-black">
                            {name}
                        </p>

                        <p
                            className={`mt-0.5 truncate text-[11px] ${
                                isDarkMode
                                    ? "text-slate-500"
                                    : "text-slate-500"
                            }`}
                        >
                            {email}
                        </p>
                    </div>

                    <div
                        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-xl ${
                            isLeft
                                ? "bg-blue-500/10 text-blue-500"
                                : "bg-purple-500/10 text-purple-500"
                        }`}
                    >
                        {isLeft ? (
                            <FaArrowLeft className="text-xs" />
                        ) : (
                            <FaArrowRight className="text-xs" />
                        )}
                    </div>
                </div>

                <div
                    className={`mt-3 rounded-xl px-3 py-2 ${
                        isDarkMode
                            ? "bg-black/10"
                            : "bg-slate-50"
                    }`}
                >
                    <p
                        className={`text-[9px] font-bold uppercase tracking-wider ${
                            isDarkMode
                                ? "text-slate-600"
                                : "text-slate-400"
                        }`}
                    >
                        Referral kodu
                    </p>

                    <p
                        className={`mt-1 break-all text-xs font-black ${
                            isLeft
                                ? "text-blue-500"
                                : "text-purple-500"
                        }`}
                    >
                        {referralCode}
                    </p>
                </div>
            </div>
        );
    };

    const BranchCard = ({
        title,
        icon: Icon,
        amount,
        color,
        bg,
    }) => (
        <div
            className={`rounded-2xl p-4 ${
                isDarkMode
                    ? "bg-black/15"
                    : "bg-slate-50"
            }`}
        >
            <div className="flex items-center gap-2">
                <div
                    className={`flex h-8 w-8 items-center justify-center rounded-lg ${bg} ${color}`}
                >
                    <Icon className="text-xs" />
                </div>

                <span className="text-xs font-bold">
                    {title}
                </span>
            </div>

            <p className="mt-3 text-lg font-black">
                {formatMoney(amount)}
            </p>
        </div>
    );

    return (
        <div
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 p-2 backdrop-blur-md sm:p-4"
            onMouseDown={(e) => {
                if (
                    e.target ===
                    e.currentTarget
                ) {
                    onClose();
                }
            }}
        >
            <div
                className={`relative flex max-h-[96vh] w-full max-w-[1380px] flex-col overflow-hidden rounded-[30px] border shadow-2xl ${
                    isDarkMode
                        ? "border-white/10 bg-[#080d18] text-white shadow-black/60"
                        : "border-white bg-slate-50 text-slate-900 shadow-slate-900/20"
                }`}
            >
                {/* =====================================================
                    PREMIUM HEADER
                ====================================================== */}

                <div
                    className={`relative shrink-0 overflow-hidden border-b ${
                        isDarkMode
                            ? "border-white/10 bg-[#0b1220]"
                            : "border-slate-200 bg-white"
                    }`}
                >
                    <div
                        className={`absolute -left-20 -top-32 h-72 w-72 rounded-full blur-3xl ${
                            isDarkMode
                                ? "bg-blue-600/15"
                                : "bg-blue-200/40"
                        }`}
                    />

                    <div
                        className={`absolute -right-20 -top-32 h-72 w-72 rounded-full blur-3xl ${
                            isDarkMode
                                ? "bg-purple-600/10"
                                : "bg-purple-200/30"
                        }`}
                    />

                    <div className="relative flex items-center justify-between px-5 py-4 sm:px-7">
                        <div className="flex items-center gap-3">
                            <div
                                className={`flex h-11 w-11 items-center justify-center rounded-2xl ${
                                    isDarkMode
                                        ? "bg-blue-500/10 text-blue-400 ring-1 ring-blue-400/20"
                                        : "bg-blue-50 text-blue-600 ring-1 ring-blue-100"
                                }`}
                            >
                                <FaShieldAlt />
                            </div>

                            <div>
                                <h2 className="text-lg font-black tracking-tight sm:text-xl">
                                    İstifadəçi məlumatları
                                </h2>

                                <p
                                    className={`text-[10px] sm:text-xs ${
                                        isDarkMode
                                            ? "text-slate-500"
                                            : "text-slate-500"
                                    }`}
                                >
                                    Ətraflı profil və qrup analitikası
                                </p>
                            </div>
                        </div>

                        <button
                            type="button"
                            onClick={onClose}
                            className={`group flex h-10 w-10 items-center justify-center rounded-xl transition-all ${
                                isDarkMode
                                    ? "bg-white/5 text-slate-400 hover:bg-red-500/10 hover:text-red-400"
                                    : "bg-slate-100 text-slate-500 hover:bg-red-50 hover:text-red-500"
                            }`}
                        >
                            <FaTimes className="transition-transform duration-300 group-hover:rotate-90" />
                        </button>
                    </div>
                </div>

                {/* =====================================================
                    BODY
                ====================================================== */}

                <div className="overflow-y-auto p-3 sm:p-5 lg:p-6">
                    {loading ? (
                        <div className="flex min-h-[550px] items-center justify-center">
                            <div className="flex flex-col items-center">
                                <div
                                    className={`flex h-20 w-20 items-center justify-center rounded-3xl ${
                                        isDarkMode
                                            ? "bg-blue-500/10"
                                            : "bg-blue-50"
                                    }`}
                                >
                                    <FaSpinner className="animate-spin text-3xl text-blue-500" />
                                </div>

                                <p
                                    className={`mt-5 text-sm font-bold ${
                                        isDarkMode
                                            ? "text-slate-400"
                                            : "text-slate-500"
                                    }`}
                                >
                                    Məlumatlar yüklənir...
                                </p>

                                <p
                                    className={`mt-1 text-[11px] ${
                                        isDarkMode
                                            ? "text-slate-600"
                                            : "text-slate-400"
                                    }`}
                                >
                                    Bir neçə saniyə çəkə bilər
                                </p>
                            </div>
                        </div>
                    ) : (
                        <>
                            {errorMessage && (
                                <div
                                    className={`mb-5 rounded-2xl border p-4 text-sm font-semibold ${
                                        isDarkMode
                                            ? "border-red-500/20 bg-red-500/10 text-red-300"
                                            : "border-red-200 bg-red-50 text-red-700"
                                    }`}
                                >
                                    {errorMessage}
                                </div>
                            )}

                            {/* =================================================
                                PROFILE HERO
                            ================================================== */}

                            <div
                                className={`relative mb-6 overflow-hidden rounded-[30px] border ${
                                    isDarkMode
                                        ? "border-white/10 bg-gradient-to-br from-blue-500/[0.08] via-white/[0.025] to-purple-500/[0.05]"
                                        : "border-slate-200 bg-white"
                                }`}
                            >
                                <div className="absolute right-0 top-0 h-40 w-40 rounded-full bg-blue-500/10 blur-3xl" />

                                <div className="relative p-5 sm:p-7">
                                    <div className="flex flex-col gap-6 lg:flex-row lg:items-center">
                                        <div className="relative mx-auto lg:mx-0">
                                            {profilePhoto ? (
                                                <img
                                                    src={profilePhoto}
                                                    alt={
                                                        displayUser.name ||
                                                        "İstifadəçi"
                                                    }
                                                    className="h-28 w-28 rounded-[28px] object-cover shadow-2xl ring-4 ring-white/10 sm:h-32 sm:w-32"
                                                />
                                            ) : (
                                                <div
                                                    className={`flex h-28 w-28 items-center justify-center rounded-[28px] shadow-xl sm:h-32 sm:w-32 ${
                                                        isDarkMode
                                                            ? "bg-slate-800 text-slate-400"
                                                            : "bg-slate-100 text-slate-400"
                                                    }`}
                                                >
                                                    <FaUser className="text-5xl" />
                                                </div>
                                            )}

                                            <div
                                                className={`absolute -bottom-2 -right-2 flex h-9 w-9 items-center justify-center rounded-xl border-4 ${
                                                    isDarkMode
                                                        ? "border-[#101725] bg-emerald-500 text-white"
                                                        : "border-white bg-emerald-500 text-white"
                                                }`}
                                            >
                                                {payment ? (
                                                    <FaCheckCircle className="text-xs" />
                                                ) : (
                                                    <FaTimesCircle className="text-xs" />
                                                )}
                                            </div>
                                        </div>

                                        <div className="min-w-0 flex-1 text-center lg:text-left">
                                            <div className="flex flex-wrap items-center justify-center gap-2 lg:justify-start">
                                                <h3 className="text-2xl font-black tracking-tight sm:text-3xl">
                                                    {displayUser.name ||
                                                        "-"}
                                                </h3>

                                                <span
                                                    className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[10px] font-black ${
                                                        payment
                                                            ? "bg-emerald-500/10 text-emerald-500"
                                                            : "bg-red-500/10 text-red-500"
                                                    }`}
                                                >
                                                    {payment ? (
                                                        <FaCheckCircle />
                                                    ) : (
                                                        <FaTimesCircle />
                                                    )}

                                                    {payment
                                                        ? "ÖDƏNİŞ AKTİVDİR"
                                                        : "ÖDƏNİŞ YOXDUR"}
                                                </span>
                                            </div>

                                            <p
                                                className={`mt-2 break-all text-sm ${
                                                    isDarkMode
                                                        ? "text-slate-400"
                                                        : "text-slate-500"
                                                }`}
                                            >
                                                {displayUser.email ||
                                                    "-"}
                                            </p>

                                            <div className="mt-4 flex flex-wrap justify-center gap-2 lg:justify-start">
                                                <span
                                                    className={`rounded-xl px-3 py-2 text-[10px] font-black ${
                                                        isDarkMode
                                                            ? "bg-blue-500/10 text-blue-400"
                                                            : "bg-blue-50 text-blue-600"
                                                    }`}
                                                >
                                                    REF:{" "}
                                                    {displayUser.referralCode ||
                                                        "-"}
                                                </span>

                                                <span
                                                    className={`rounded-xl px-3 py-2 text-[10px] font-black ${
                                                        isDarkMode
                                                            ? "bg-white/5 text-slate-300"
                                                            : "bg-slate-100 text-slate-600"
                                                    }`}
                                                >
                                                    QOL:{" "}
                                                    {getSideText(
                                                        displayUser.side
                                                    )}
                                                </span>

                                                <span
                                                    className={`rounded-xl px-3 py-2 text-[10px] font-black ${
                                                        isDarkMode
                                                            ? "bg-purple-500/10 text-purple-400"
                                                            : "bg-purple-50 text-purple-600"
                                                    }`}
                                                >
                                                    RÜTBƏ:{" "}
                                                    {getRank()}
                                                </span>
                                            </div>
                                        </div>

                                        <div
                                            className={`hidden min-w-[180px] rounded-3xl p-5 text-center lg:block ${
                                                isDarkMode
                                                    ? "bg-black/15"
                                                    : "bg-slate-50"
                                            }`}
                                        >
                                            <FaCoins className="mx-auto text-2xl text-amber-500" />

                                            <p
                                                className={`mt-2 text-[10px] font-bold uppercase tracking-wider ${
                                                    isDarkMode
                                                        ? "text-slate-500"
                                                        : "text-slate-400"
                                                }`}
                                            >
                                                Ümumi qazanc
                                            </p>

                                            <p className="mt-1 text-2xl font-black">
                                                {formatMoney(
                                                    totalPersonalEarnings
                                                )}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* =================================================
                                MAIN STATS
                            ================================================== */}

                            <div className="mb-7 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
                                <StatCard
                                    icon={FaMoneyBillWave}
                                    title="Maaş"
                                    value={formatMoney(salary)}
                                    subtitle="Şəxsi maaş"
                                    iconClass={
                                        isDarkMode
                                            ? "text-amber-400"
                                            : "text-amber-600"
                                    }
                                    iconBg={
                                        isDarkMode
                                            ? "bg-amber-500/10"
                                            : "bg-amber-50"
                                    }
                                />

                                <StatCard
                                    icon={FaWallet}
                                    title="Qazanc"
                                    value={formatMoney(
                                        referralEarnings
                                    )}
                                    subtitle="Şəxsi referans qazancı"
                                    iconClass={
                                        isDarkMode
                                            ? "text-emerald-400"
                                            : "text-emerald-600"
                                    }
                                    iconBg={
                                        isDarkMode
                                            ? "bg-emerald-500/10"
                                            : "bg-emerald-50"
                                    }
                                />

                                <StatCard
                                    icon={FaUsers}
                                    title="Şəxsi dəvət"
                                    value={getReferralCount()}
                                    subtitle="Birbaşa dəvət olunanlar"
                                    iconClass={
                                        isDarkMode
                                            ? "text-blue-400"
                                            : "text-blue-600"
                                    }
                                    iconBg={
                                        isDarkMode
                                            ? "bg-blue-500/10"
                                            : "bg-blue-50"
                                    }
                                />

                                <StatCard
                                    icon={FaLayerGroup}
                                    title="Qrup sayı"
                                    value={getGroupCount()}
                                    subtitle="Ümumi qrup"
                                    iconClass={
                                        isDarkMode
                                            ? "text-purple-400"
                                            : "text-purple-600"
                                    }
                                    iconBg={
                                        isDarkMode
                                            ? "bg-purple-500/10"
                                            : "bg-purple-50"
                                    }
                                />
                            </div>

                            {/* =================================================
                                GROUP FINANCIAL
                            ================================================== */}

                            <div className="mb-8">
                                <SectionTitle
                                    icon={FaUsers}
                                    title="Qrup maliyyə məlumatları"
                                    description="Sol və sağ qol üzrə ümumi maliyyə göstəriciləri"
                                />

                                <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                                    <div
                                        className={`rounded-[28px] border p-5 sm:p-6 ${
                                            isDarkMode
                                                ? "border-white/10 bg-white/[0.035]"
                                                : "border-slate-200 bg-white"
                                        }`}
                                    >
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <p
                                                    className={`text-[10px] font-bold uppercase tracking-wider ${
                                                        isDarkMode
                                                            ? "text-slate-500"
                                                            : "text-slate-400"
                                                    }`}
                                                >
                                                    Qrup maaşı
                                                </p>

                                                <p className="mt-1 text-3xl font-black tracking-tight">
                                                    {formatMoney(
                                                        totalGroupSalary
                                                    )}
                                                </p>
                                            </div>

                                            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-amber-500/10 text-amber-500">
                                                <FaMoneyBillWave />
                                            </div>
                                        </div>

                                        <div className="mt-5 grid grid-cols-2 gap-3">
                                            <BranchCard
                                                title="Sol qol"
                                                icon={FaArrowLeft}
                                                amount={leftSalary}
                                                color="text-blue-500"
                                                bg="bg-blue-500/10"
                                            />

                                            <BranchCard
                                                title="Sağ qol"
                                                icon={FaArrowRight}
                                                amount={rightSalary}
                                                color="text-purple-500"
                                                bg="bg-purple-500/10"
                                            />
                                        </div>
                                    </div>

                                    <div
                                        className={`rounded-[28px] border p-5 sm:p-6 ${
                                            isDarkMode
                                                ? "border-white/10 bg-white/[0.035]"
                                                : "border-slate-200 bg-white"
                                        }`}
                                    >
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <p
                                                    className={`text-[10px] font-bold uppercase tracking-wider ${
                                                        isDarkMode
                                                            ? "text-slate-500"
                                                            : "text-slate-400"
                                                    }`}
                                                >
                                                    Qrup qazancı
                                                </p>

                                                <p className="mt-1 text-3xl font-black tracking-tight">
                                                    {formatMoney(
                                                        totalGroupEarnings
                                                    )}
                                                </p>
                                            </div>

                                            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-500/10 text-emerald-500">
                                                <FaWallet />
                                            </div>
                                        </div>

                                        <div className="mt-5 grid grid-cols-2 gap-3">
                                            <BranchCard
                                                title="Sol qol"
                                                icon={FaArrowLeft}
                                                amount={leftEarnings}
                                                color="text-blue-500"
                                                bg="bg-blue-500/10"
                                            />

                                            <BranchCard
                                                title="Sağ qol"
                                                icon={FaArrowRight}
                                                amount={rightEarnings}
                                                color="text-purple-500"
                                                bg="bg-purple-500/10"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* =================================================
                                PERSONAL INFORMATION
                            ================================================== */}

                            <div className="mb-8">
                                <SectionTitle
                                    icon={FaUser}
                                    title="Şəxsi məlumatlar"
                                    description="İstifadəçinin əsas profil məlumatları"
                                />

                                <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
                                    <InfoItem
                                        icon={FaUser}
                                        label="Ad"
                                        value={
                                            displayUser.name ||
                                            "-"
                                        }
                                    />

                                    <InfoItem
                                        icon={FaEnvelope}
                                        label="Email"
                                        value={
                                            displayUser.email ||
                                            "-"
                                        }
                                    />

                                    <InfoItem
                                        icon={FaPhone}
                                        label="Telefon"
                                        value={
                                            displayUser.phone ||
                                            "-"
                                        }
                                    />

                                    <InfoItem
                                        icon={FaIdCard}
                                        label="Fin kod"
                                        value={
                                            displayUser.finCode ||
                                            "-"
                                        }
                                    />

                                    <InfoItem
                                        icon={FaVenusMars}
                                        label="Cins"
                                        value={
                                            displayUser.gender ||
                                            "-"
                                        }
                                    />

                                    <InfoItem
                                        icon={FaWallet}
                                        label="Kart"
                                        value={
                                            displayUser.card ||
                                            "Kart yoxdur"
                                        }
                                    />

                                    <InfoItem
                                        icon={FaUsers}
                                        label="Qol"
                                        value={getSideText(
                                            displayUser.side
                                        )}
                                    />

                                    <InfoItem
                                        icon={FaTrophy}
                                        label="Maaşa görə rütbəsi"
                                        value={getRank()}
                                    />

                                    <InfoItem
                                        icon={FaCalendarAlt}
                                        label="Qeydiyyat tarixi"
                                        value={formatDate(
                                            displayUser.createdAt
                                        )}
                                    />
                                </div>
                            </div>

                            {/* =================================================
                                REFERRAL
                            ================================================== */}

                            <div className="mb-8">
                                <SectionTitle
                                    icon={FaLink}
                                    title="Referans məlumatları"
                                    description="İstifadəçinin referans əlaqələri və linki"
                                />

                                <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                                    <PersonCard
                                        title="Kimə bağlıdır"
                                        person={referrerData}
                                    />

                                    <PersonCard
                                        title="Link sahibi"
                                        person={linkOwnerData}
                                    />
                                </div>

                                <div
                                    className={`mt-4 overflow-hidden rounded-[28px] border ${
                                        isDarkMode
                                            ? "border-white/10 bg-white/[0.035]"
                                            : "border-slate-200 bg-white"
                                    }`}
                                >
                                    <div className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between">
                                        <div>
                                            <p
                                                className={`text-[10px] font-bold uppercase tracking-wider ${
                                                    isDarkMode
                                                        ? "text-slate-500"
                                                        : "text-slate-400"
                                                }`}
                                            >
                                                Ümumi şəxsi qazanc
                                            </p>

                                            <p className="mt-1 text-3xl font-black">
                                                {formatMoney(
                                                    totalPersonalEarnings
                                                )}
                                            </p>
                                        </div>

                                        <div
                                            className={`rounded-2xl px-4 py-3 text-xs font-bold ${
                                                isDarkMode
                                                    ? "bg-black/15 text-slate-400"
                                                    : "bg-slate-50 text-slate-500"
                                            }`}
                                        >
                                            {formatMoney(
                                                salary
                                            )}{" "}
                                            maaş
                                            <span className="mx-2 text-blue-500">
                                                +
                                            </span>
                                            {formatMoney(
                                                referralEarnings
                                            )}{" "}
                                            qazanc
                                        </div>
                                    </div>
                                </div>

                                <div
                                    className={`mt-4 rounded-[28px] border p-5 ${
                                        isDarkMode
                                            ? "border-white/10 bg-white/[0.035]"
                                            : "border-slate-200 bg-white"
                                    }`}
                                >
                                    <div className="flex items-start gap-3">
                                        <div
                                            className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl ${
                                                isDarkMode
                                                    ? "bg-blue-500/10 text-blue-400"
                                                    : "bg-blue-50 text-blue-600"
                                            }`}
                                        >
                                            <FaLink />
                                        </div>

                                        <div className="min-w-0 flex-1">
                                            <p className="text-sm font-black">
                                                Referans linki
                                            </p>

                                            <div className="mt-3 flex flex-col gap-3 xl:flex-row">
                                                <div
                                                    className={`min-w-0 flex-1 break-all rounded-2xl border px-4 py-3 text-xs font-medium ${
                                                        isDarkMode
                                                            ? "border-white/10 bg-black/10 text-slate-400"
                                                            : "border-slate-200 bg-slate-50 text-slate-600"
                                                    }`}
                                                >
                                                    {referralLink}
                                                </div>

                                                <div className="flex gap-2">
                                                    <button
                                                        type="button"
                                                        onClick={
                                                            copyReferralLink
                                                        }
                                                        disabled={
                                                            !displayUser.referralCode
                                                        }
                                                        className={`flex items-center justify-center gap-2 rounded-2xl px-5 py-3 text-xs font-black transition-all ${
                                                            copied
                                                                ? "bg-emerald-500 text-white shadow-lg shadow-emerald-500/20"
                                                                : "bg-blue-600 text-white shadow-lg shadow-blue-600/20 hover:bg-blue-700"
                                                        } disabled:cursor-not-allowed disabled:opacity-40`}
                                                    >
                                                        {copied ? (
                                                            <>
                                                                <FaCheckCircle />
                                                                Kopyalandı
                                                            </>
                                                        ) : (
                                                            <>
                                                                <FaCopy />
                                                                Kopyala
                                                            </>
                                                        )}
                                                    </button>

                                                    {displayUser.referralCode && (
                                                        <a
                                                            href={
                                                                referralLink
                                                            }
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className={`flex h-11 w-11 items-center justify-center rounded-2xl transition ${
                                                                isDarkMode
                                                                    ? "bg-white/5 text-slate-300 hover:bg-white/10"
                                                                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                                                            }`}
                                                            title="Linki aç"
                                                        >
                                                            <FaExternalLinkAlt className="text-xs" />
                                                        </a>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* =================================================
                                LEFT / RIGHT
                            ================================================== */}

                            <div className="mb-8">
                                <SectionTitle
                                    icon={FaUsers}
                                    title="Sol və sağ qol"
                                    description="İstifadəçinin birbaşa sol və sağ qolundakı üzvlər"
                                />

                                <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
                                    {/* LEFT */}

                                    <div
                                        className={`overflow-hidden rounded-[28px] border ${
                                            isDarkMode
                                                ? "border-blue-500/10 bg-gradient-to-b from-blue-500/[0.04] to-transparent"
                                                : "border-blue-100 bg-white"
                                        }`}
                                    >
                                        <div
                                            className={`flex items-center justify-between border-b p-5 ${
                                                isDarkMode
                                                    ? "border-white/10"
                                                    : "border-slate-100"
                                            }`}
                                        >
                                            <div className="flex items-center gap-3">
                                                <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-blue-500/10 text-blue-500">
                                                    <FaArrowLeft />
                                                </div>

                                                <div>
                                                    <h4 className="font-black">
                                                        Sol qol
                                                    </h4>

                                                    <p
                                                        className={`text-[10px] font-semibold ${
                                                            isDarkMode
                                                                ? "text-slate-500"
                                                                : "text-slate-400"
                                                        }`}
                                                    >
                                                        {
                                                            leftMembers.length
                                                        }{" "}
                                                        istifadəçi
                                                    </p>
                                                </div>
                                            </div>

                                            <span className="rounded-full bg-blue-500/10 px-3 py-1.5 text-[10px] font-black text-blue-500">
                                                SOL
                                            </span>
                                        </div>

                                        <div className="p-4">
                                            {leftMembers.length >
                                            0 ? (
                                                <div className="space-y-3">
                                                    {leftMembers.map(
                                                        (
                                                            member,
                                                            index
                                                        ) => (
                                                            <SideUserCard
                                                                key={
                                                                    member?._id ||
                                                                    member?.id ||
                                                                    member?.referralCode ||
                                                                    `left-${index}`
                                                                }
                                                                member={
                                                                    member
                                                                }
                                                                side="left"
                                                            />
                                                        )
                                                    )}
                                                </div>
                                            ) : (
                                                <div
                                                    className={`rounded-2xl border border-dashed p-8 text-center ${
                                                        isDarkMode
                                                            ? "border-white/10 text-slate-600"
                                                            : "border-slate-200 text-slate-400"
                                                    }`}
                                                >
                                                    <FaArrowLeft className="mx-auto mb-3 text-xl opacity-40" />

                                                    <p className="text-xs font-semibold">
                                                        Sol qolda istifadəçi yoxdur.
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* RIGHT */}

                                    <div
                                        className={`overflow-hidden rounded-[28px] border ${
                                            isDarkMode
                                                ? "border-purple-500/10 bg-gradient-to-b from-purple-500/[0.04] to-transparent"
                                                : "border-purple-100 bg-white"
                                        }`}
                                    >
                                        <div
                                            className={`flex items-center justify-between border-b p-5 ${
                                                isDarkMode
                                                    ? "border-white/10"
                                                    : "border-slate-100"
                                            }`}
                                        >
                                            <div className="flex items-center gap-3">
                                                <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-purple-500/10 text-purple-500">
                                                    <FaArrowRight />
                                                </div>

                                                <div>
                                                    <h4 className="font-black">
                                                        Sağ qol
                                                    </h4>

                                                    <p
                                                        className={`text-[10px] font-semibold ${
                                                            isDarkMode
                                                                ? "text-slate-500"
                                                                : "text-slate-400"
                                                        }`}
                                                    >
                                                        {
                                                            rightMembers.length
                                                        }{" "}
                                                        istifadəçi
                                                    </p>
                                                </div>
                                            </div>

                                            <span className="rounded-full bg-purple-500/10 px-3 py-1.5 text-[10px] font-black text-purple-500">
                                                SAĞ
                                            </span>
                                        </div>

                                        <div className="p-4">
                                            {rightMembers.length >
                                            0 ? (
                                                <div className="space-y-3">
                                                    {rightMembers.map(
                                                        (
                                                            member,
                                                            index
                                                        ) => (
                                                            <SideUserCard
                                                                key={
                                                                    member?._id ||
                                                                    member?.id ||
                                                                    member?.referralCode ||
                                                                    `right-${index}`
                                                                }
                                                                member={
                                                                    member
                                                                }
                                                                side="right"
                                                            />
                                                        )
                                                    )}
                                                </div>
                                            ) : (
                                                <div
                                                    className={`rounded-2xl border border-dashed p-8 text-center ${
                                                        isDarkMode
                                                            ? "border-white/10 text-slate-600"
                                                            : "border-slate-200 text-slate-400"
                                                    }`}
                                                >
                                                    <FaArrowRight className="mx-auto mb-3 text-xl opacity-40" />

                                                    <p className="text-xs font-semibold">
                                                        Sağ qolda istifadəçi yoxdur.
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* =================================================
                                SYSTEM
                            ================================================== */}

                            <div className="mb-2">
                                <SectionTitle
                                    icon={FaShieldAlt}
                                    title="Sistem məlumatları"
                                    description="İstifadəçi sistem identifikasiyası və status məlumatları"
                                />

                                <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
                                    <InfoItem
                                        icon={FaIdCard}
                                        label="İstifadəçi ID"
                                        value={
                                            displayUser._id ||
                                            displayUser.id ||
                                            "-"
                                        }
                                    />

                                    <InfoItem
                                        icon={FaLink}
                                        label="Referral kodu"
                                        value={
                                            displayUser.referralCode ||
                                            "-"
                                        }
                                    />

                                    <InfoItem
                                        icon={FaUsers}
                                        label="Qol"
                                        value={getSideText(
                                            displayUser.side
                                        )}
                                    />

                                    <InfoItem
                                        icon={FaCalendarAlt}
                                        label="Yaradılma tarixi"
                                        value={formatDate(
                                            displayUser.createdAt
                                        )}
                                    />

                                    <InfoItem
                                        icon={FaCalendarAlt}
                                        label="Yenilənmə tarixi"
                                        value={formatDate(
                                            displayUser.updatedAt
                                        )}
                                    />

                                    <InfoItem
                                        icon={
                                            payment
                                                ? FaCheckCircle
                                                : FaTimesCircle
                                        }
                                        label="Ödəniş statusu"
                                        value={
                                            payment
                                                ? "Aktiv"
                                                : "Aktiv deyil"
                                        }
                                    />
                                </div>
                            </div>
                        </>
                    )}
                </div>

                {/* =====================================================
                    FOOTER
                ====================================================== */}

                <div
                    className={`flex shrink-0 items-center justify-between border-t px-5 py-4 sm:px-7 ${
                        isDarkMode
                            ? "border-white/10 bg-[#0b1220]"
                            : "border-slate-200 bg-white"
                    }`}
                >
                    <div className="hidden items-center gap-2 sm:flex">
                        <span className="h-2 w-2 rounded-full bg-emerald-500" />

                        <span
                            className={`text-[10px] font-bold ${
                                isDarkMode
                                    ? "text-slate-600"
                                    : "text-slate-400"
                            }`}
                        >
                            Profil məlumatları
                        </span>
                    </div>

                    <button
                        type="button"
                        onClick={onClose}
                        className={`rounded-2xl px-6 py-3 text-xs font-black transition-all ${
                            isDarkMode
                                ? "bg-white/5 text-slate-200 hover:bg-white/10"
                                : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                        }`}
                    >
                        Bağla
                    </button>
                </div>
            </div>
        </div>
    );
};

export default UserDetails;
